import { AgGridReact } from 'ag-grid-react';
import {
  PartnerCell,
  ResultCell,
  OpponentsCell,
  createDateCell,
  DateCell,
  ScoreCell,
  MoreInfoCell,
  TeamCell,
  RoundPositionCell
} from './tableCells';
import { ResultsTableModal } from '../modals';
import styles from './resultsTable.module.scss';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import { ModalProvider } from '../../context/results-modal-context';
import { useMobile } from '../../hooks/use-mobile';
import { ColDef } from 'ag-grid-community';

interface ResultsTableProps {
  tableWidth: number;
  data: any;
  id?: string;
  type?: string;
  thisPlayer: {
    standardGivenName: string;
    standardFamilyName: string;
  };
}

interface ITeam {
  name: string;
}

const ShortDateCell = createDateCell('MMM DD, YY');

const ResultsTable = ({ tableWidth, data, id, type, thisPlayer }: ResultsTableProps) => {
  const { isMobile } = useMobile(1024);

  const handleTeamSort = (valueA: ITeam, valueB: ITeam) => {
    const nameA = valueA?.name.toLowerCase();
    const nameB = valueB?.name.toLowerCase();

    if (nameA > nameB) {
      return 1;
    }
    if (nameA < nameB) {
      return -1;
    }

    return 0;
  };

  const onGridReady = () => {
    console.log('');
  };

  let columnDefs: ColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      cellRenderer: DateCell,
      sortable: true,
      sort: 'asc',
      cellClass: styles.cell,
      flex: 1.2
    },
    {
      field: 'thisPlayers',
      headerName: 'Partner',
      cellRenderer: PartnerCell,
      cellRendererParams: { playerId: id },
      cellClass: styles.cell,
      flex: 1.8
    },
    {
      field: 'opponentPlayers',
      headerName: 'Opponents',
      cellRenderer: OpponentsCell,
      sortable: true,
      comparator: (valueA, valueB) => {
        if (valueA?.opponent && valueB?.opponent) {
          const firstOpponentA = valueA?.opponent[0]?.person;
          const firstOpponentB = valueB?.opponent[0]?.person;

          if (firstOpponentA?.nativeFamilyName === firstOpponentB?.nativeFamilyName) return 0;
          return firstOpponentA?.nativeFamilyName > firstOpponentB?.nativeFamilyName ? 1 : -1;
        }

        return 0;
      },
      cellClass: styles.cell,
      flex: 2
    },
    {
      field: 'team',
      headerName: 'Team',
      cellRenderer: TeamCell,
      comparator: handleTeamSort,
      sortable: true,
      cellClass: styles.cell,
      flex: 3
    },
    {
      field: 'thisPlayerWon',
      headerName: 'W - L',
      cellRenderer: ResultCell,
      cellClass: styles.cell,
      flex: 1
    },
    {
      field: 'score',
      headerName: 'Score',
      cellRenderer: ScoreCell,
      cellClass: styles.cell,
      flex: 2
    },
    {
      field: 'roundsAndPositions',
      headerName: 'Rnd/Pos',
      cellRenderer: RoundPositionCell,
      cellClass: styles.cell,
      flex: 1
    }
  ];

  let columnDefsMobile: ColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      cellRenderer: ShortDateCell,
      sortable: true,
      sort: 'asc',
      cellClass: styles.cell,
      flex: 2
    },
    {
      field: 'opponentPlayers',
      headerName: 'Opponents',
      cellRenderer: OpponentsCell,
      sortable: true,
      comparator: (valueA, valueB) => {
        if (valueA?.opponent && valueB?.opponent) {
          const firstOpponentA = valueA?.opponent[0]?.person;
          const firstOpponentB = valueB?.opponent[0]?.person;

          if (firstOpponentA?.nativeFamilyName === firstOpponentB?.nativeFamilyName) return 0;
          return firstOpponentA?.nativeFamilyName > firstOpponentB?.nativeFamilyName ? 1 : -1;
        }

        return 0;
      },
      cellClass: styles.cell,
      flex: 3
    },
    {
      field: 'thisPlayerWon',
      headerName: 'W - L',
      cellRenderer: ResultCell,
      cellClass: styles.cell,
      flex: 1.5
    },
    {
      field: 'moreInfo',
      headerName: '',
      cellRenderer: MoreInfoCell,
      cellClass: styles.cell,
      flex: 1
    }
  ];

  if (type === 'singles') {
    columnDefs = [
      {
        field: 'date',
        headerName: 'Date',
        cellRenderer: DateCell,
        sortable: true,
        sort: 'asc',
        cellClass: styles.cell,
        flex: 1.5
      },
      {
        field: 'opponentPlayers',
        headerName: 'Opponent',
        cellRenderer: OpponentsCell,
        sortable: true,
        comparator: (valueA, valueB) => {
          const personA = valueA.opponent[0]?.person;
          const personB = valueB.opponent[0]?.person;

          if (personA.nativeFamilyName === personB.nativeFamilyName) return 0;

          return personA.nativeFamilyName > personB.nativeFamilyName ? 1 : -1;
        },
        cellClass: styles.cell,
        flex: 1.5
      },
      {
        field: 'team',
        headerName: 'Team',
        cellRenderer: TeamCell,
        comparator: handleTeamSort,
        sortable: true,
        cellClass: styles.cell,
        flex: 3
      },
      {
        field: 'thisPlayerWon',
        headerName: 'W - L',
        cellRenderer: ResultCell,
        cellClass: styles.cell,
        flex: 1
      },
      {
        field: 'score',
        headerName: 'Score',
        cellRenderer: ScoreCell,
        cellClass: styles.cell,
        flex: 2
      },
      {
        field: 'roundsAndPositions',
        headerName: 'Rnd/Pos',
        cellRenderer: RoundPositionCell,
        cellClass: styles.cell,
        flex: 1
      }
    ];

    columnDefsMobile = [
      {
        field: 'date',
        headerName: 'Date',
        cellRenderer: ShortDateCell,
        sortable: true,
        sort: 'asc',
        cellClass: styles.cell,
        flex: 2
      },
      {
        field: 'opponentPlayers',
        headerName: 'Opponent',
        cellRenderer: OpponentsCell,
        sortable: true,
        comparator: (valueA, valueB) => {
          const personA = valueA.opponent[0]?.person;
          const personB = valueB.opponent[0]?.person;

          if (personA.nativeFamilyName === personB.nativeFamilyName) return 0;

          return personA.nativeFamilyName > personB.nativeFamilyName ? 1 : -1;
        },
        cellClass: styles.cell,
        flex: 3
      },
      {
        field: 'thisPlayerWon',
        headerName: 'W - L',
        cellRenderer: ResultCell,
        cellClass: styles.cell,
        flex: 1.5
      },
      {
        field: 'moreInfo',
        headerName: '',
        cellRenderer: MoreInfoCell,
        cellClass: styles.cell,
        flex: 1
      }
    ];
  }

  return (
    <>
      <div className="ag-theme-alpine alpineOverrides">
        <AgGridReact
          rowData={data}
          columnDefs={isMobile ? columnDefsMobile : columnDefs}
          rowHeight={60}
          headerHeight={40}
          domLayout="autoHeight"
          onGridReady={onGridReady}
          suppressRowHoverHighlight={true}
          sortingOrder={['desc', 'asc']}
        ></AgGridReact>
      </div>
      {isMobile && <ResultsTableModal thisPlayer={thisPlayer} type={type} />}
    </>
  );
};

const ResultsTableWithModal = (props: ResultsTableProps) => (
  <ModalProvider>
    <ResultsTable {...props} />
  </ModalProvider>
);

export default ResultsTableWithModal;
